import type { Id } from '../api/types';
import { getBaseApiUrl } from '../config/environment';

export interface WorkflowSocketHandlers {
  onMessage?: (data: unknown) => void;
  onOpen?: () => void;
  onClose?: (e: CloseEvent) => void;
  onError?: (e: Event) => void;
}

export class WebsocketService {
  private socket?: WebSocket;
  private reconnectDelay: number;
  private reconnectTimeout?: ReturnType<typeof setTimeout>;
  private manualClose = false;
  private baseUrl?: string;
  private handlers: WorkflowSocketHandlers = {};
  private connecting = false;
  private requiresToken = false;
  private connectionVersion = 0;

  constructor(reconnectDelay = 1000) {
    this.reconnectDelay = reconnectDelay;
  }

  private getAppOrigin(): string {
    if (globalThis.location?.origin) {
      return globalThis.location.origin;
    }
    return `${globalThis.location.protocol}//${globalThis.location.host}`;
  }

  private getHttpOriginForWebSocket(url: URL): string {
    const protocol = url.protocol === 'wss:' ? 'https:' : 'http:';
    return `${protocol}//${url.host}`;
  }

  private buildWorkflowSocketUrl(id: Id): URL {
    const wsUrl = import.meta.env.VITE_WS_URL;
    if (wsUrl) {
      const url = new URL(wsUrl);
      url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
      url.pathname = `/ws/workflows/${id}`;
      url.search = '';
      url.hash = '';
      return url;
    }

    const apiUrl = new URL(getBaseApiUrl());
    const protocol = apiUrl.protocol === 'https:' ? 'wss:' : 'ws:';
    return new URL(`${protocol}//${apiUrl.host}/ws/workflows/${id}`);
  }

  connectToWorkflow(id: Id, handlers: WorkflowSocketHandlers = {}) {
    const socketUrl = this.buildWorkflowSocketUrl(id);
    const baseUrl = socketUrl.toString();

    if (this.baseUrl === baseUrl) {
      this.handlers = handlers;
      if (this.connecting) {
        return;
      }
      if (this.socket?.readyState === WebSocket.OPEN) {
        handlers.onOpen?.();
        return this.socket;
      }
    }

    this.disconnect();
    this.connecting = true;
    this.connectionVersion += 1;
    const connectionVersion = this.connectionVersion;
    this.handlers = handlers;

    this.baseUrl = baseUrl;
    this.requiresToken =
      this.getHttpOriginForWebSocket(socketUrl) !== this.getAppOrigin();

    this.manualClose = false;
    if (this.requiresToken) {
      return this.openWithToken(connectionVersion);
    }
    return this.open();
  }

  disconnect() {
    if (this.socket) {
    }
    this.manualClose = true;
    this.connecting = false; // Reset connecting flag
    this.requiresToken = false;
    this.baseUrl = undefined;
    this.connectionVersion += 1;
    clearTimeout(this.reconnectTimeout);

    // Close existing socket if it exists
    if (this.socket) {
      this.socket.onclose = null; // Remove the close handler to prevent reconnection
      this.socket.close();
    }
    this.socket = undefined;
  }

  private open(url = this.baseUrl) {
    if (!url) return;
    const socket = new WebSocket(url);
    socket.onopen = () => {
      this.connecting = false; // Reset connecting flag on successful connection
      this.handlers.onOpen?.();
    };
    socket.onclose = (e) => {
      this.connecting = false; // Reset connecting flag on close
      this.handlers.onClose?.(e);
      if (!this.manualClose) {
        const connectionVersion = this.connectionVersion;
        this.reconnectTimeout = setTimeout(() => {
          if (connectionVersion !== this.connectionVersion) {
            return;
          }
          this.connecting = true;
          if (this.requiresToken) {
            void this.openWithToken(connectionVersion);
            return;
          }
          this.open();
        }, this.reconnectDelay);
      }
    };
    socket.onerror = (e) => {
      this.connecting = false; // Reset connecting flag on error
      this.handlers.onError?.(e);
    };
    socket.onmessage = (ev) => {
      try {
        this.handlers.onMessage?.(JSON.parse(ev.data));
      } catch {
        this.handlers.onMessage?.(ev.data);
      }
    };
    this.socket = socket;
    return socket;
  }

  private async openWithToken(connectionVersion: number) {
    try {
      if (!this.baseUrl) return;
      const token = await this.fetchStreamToken();
      if (
        this.manualClose ||
        !this.baseUrl ||
        connectionVersion !== this.connectionVersion
      )
        return;
      const url = new URL(this.baseUrl);
      url.searchParams.set('token', token);
      return this.open(url.toString());
    } catch (error) {
      if (connectionVersion !== this.connectionVersion) {
        return;
      }
      this.connecting = false;
      const event = new Event('error') as Event & { error?: unknown };
      event.error = error;
      this.handlers.onError?.(event);
    }
  }

  private async fetchStreamToken(): Promise<string> {
    const tokenRes = await fetch(
      `${getBaseApiUrl().replace(/\/$/, '')}/auth/stream-token`,
      { credentials: 'include' }
    );
    if (!tokenRes.ok) {
      const body = await tokenRes.text();
      throw new Error(body?.trim() || 'Failed to get stream token');
    }
    const { token } = (await tokenRes.json()) as { token?: unknown };
    if (typeof token !== 'string' || token.length === 0) {
      throw new Error('Failed to get stream token');
    }
    return token;
  }
}

export const websocketService = new WebsocketService();
